// SC-1752 — draft (sessionStorage) and "already answered" marker (localStorage).
// Every access is wrapped: private mode / disabled storage must never break the page.
import type { AnswerMap } from './types';

export interface SurveyDraft {
  /** Version the answers were given on; informational — a republish does not strand the draft. */
  version: number;
  submissionId: string;
  answers: AnswerMap;
  /** -1 = opening screen, otherwise the index of the visible section. */
  step: number;
  language: string;
}

const draftKey = (surveyUuid: string) => `survey-draft:${surveyUuid}`;
const doneKey = (surveyUuid: string) => `survey-done:${surveyUuid}`;

export function loadDraft(surveyUuid: string): SurveyDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(surveyUuid));
    if (!raw) return null;
    const d = JSON.parse(raw) as SurveyDraft;
    if (typeof d?.submissionId !== 'string' || typeof d.answers !== 'object' || !d.answers) {
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function saveDraft(surveyUuid: string, draft: SurveyDraft): void {
  try {
    sessionStorage.setItem(draftKey(surveyUuid), JSON.stringify(draft));
  } catch {
    /* storage unavailable — the draft is a convenience only */
  }
}

export function clearDraft(surveyUuid: string): void {
  try {
    sessionStorage.removeItem(draftKey(surveyUuid));
  } catch {
    /* ignore */
  }
}

export function isDone(surveyUuid: string): boolean {
  try {
    return localStorage.getItem(doneKey(surveyUuid)) !== null;
  } catch {
    return false;
  }
}

export function markDone(surveyUuid: string): void {
  try {
    localStorage.setItem(doneKey(surveyUuid), '1');
  } catch {
    /* ignore */
  }
}

export function clearDone(surveyUuid: string): void {
  try {
    localStorage.removeItem(doneKey(surveyUuid));
  } catch {
    /* ignore */
  }
}

export function newSubmissionId(): string {
  return crypto.randomUUID();
}
